import React, { useState } from 'react';
import { MessageSquare, Send, Eye, Users, Star, AlertCircle, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import FeedbackList from './FeedbackList';
import Logo from '../../public/ketaqwaan-logo.png'; // Adjust the path as necessary

const FeedbackForm = () => {
  const [selectedType, setSelectedType] = useState('');
  const [feedback, setFeedback] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showFeedbackList, setShowFeedbackList] = useState(false);

  const API_URL = "/api";

  const feedbackTypes = [
    { id: 'kritik', label: 'Kritik', icon: AlertCircle, color: 'border-red-500 bg-red-50 text-red-700' },
    { id: 'saran', label: 'Saran', icon: MessageSquare, color: 'border-green-500 bg-green-50 text-green-700' },
    { id: 'apresiasi', label: 'Apresiasi', icon: Star, color: 'border-yellow-500 bg-yellow-50 text-yellow-700' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedType) {
      toast.error('Silakan pilih jenis feedback terlebih dahulu');
      return;
    }

    if (feedback.trim().length < 5) {
      toast.error('Feedback minimal 5 karakter');
      return;
    }

    if (feedback.trim().length > 500) {
      toast.error('Feedback maksimal 500 karakter');
      return;
    }

    setIsSubmitting(true);

    try {
      const response = await fetch(`${API_URL}/kotak-saran`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          type: selectedType,
          feedback: feedback.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to submit feedback');
      }

      toast.success('Terima kasih atas feedback Anda!');
      setFeedback('');
      setSelectedType('');
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error('Terjadi kesalahan, silakan coba lagi');
    } finally {
      setIsSubmitting(false);
    }
  };

  const clientUrl= import.meta.env.VITE_CLIENT_URL

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-indigo-100 py-12 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="lg:w-24 lg:h-24 w-20 h-20 mx-auto mb-6 flex items-center justify-center">
            <img src={Logo}  alt="Logo SIE 1 Ketaqwaan MAN 1 JEMBER" className="lg:w-20 lg:h-20 w-15 h-15" />
            {/* <MessageSquare className="lg:w-12 lg:h-12 w-8 h-8  text-teal-600" /> */}
          </div>
          <h1 className="lg:text-4xl text-2xl font-bold text-teal-900 mb-4">SIE 1 KETAQWAAN</h1>
          <p className="lg:text-xl text-xs text-gray-600">Silakan memberi masukan kepada kami 😊</p>
        </div>

        {/* Feedback Form */}
        <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Feedback Type Selection */}
            <div>
              <label className="block lg:text-sm text-xs font-semibold text-gray-700 mb-4">
                Pilih Jenis Feedback
              </label>
              <div className="flex flex-row gap-3 lg:gap-4">
                {feedbackTypes.map(({ id, label, icon: Icon, color }) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => setSelectedType(id)}
                    className={`
                      flex-1 relative lg:w-0 w-5 px-3 py-3 lg:px-4 lg:py-4 rounded-xl border-2 transition-all duration-200
                      ${selectedType === id 
                        ? color 
                        : 'border-gray-200 hover:border-gray-300 text-gray-700'
                      }
                    `}
                  >
                    <div className="flex flex-col items-center space-y-1 lg:space-y-2">
                      <div className={`p-2 lg:p-3 rounded-lg ${selectedType === id ? color.split(' ')[1] : 'bg-gray-100'}`}>
                        <Icon className={`w-3 h-3 lg:w-5 lg:h-5 ${selectedType === id ? color.split(' ')[2] : 'text-gray-600'}`} />
                      </div>
                      <span className="text-xs lg:text-sm font-medium">{label}</span>
                    </div>
                    {selectedType === id && (
                      <CheckCircle2 className="absolute top-2 right-2 w-4 h-4 text-teal-500" />
                    )}
                  </button>
                ))}
              </div>

            </div>
            <div>
              <label className="block lg:text-sm text-xs font-semibold text-gray-700 mb-2">
                Tulis Feedback Anda
              </label>
              <textarea
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
                placeholder="Berikan semua Kritik / Saran / Apresiasi..."
                rows={5}
                className="w-full px-4 py-3 text-xs lg:text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none transition-all duration-200"
                required
              />
              <div className="flex justify-between items-center mt-2">
                <span className="lg:text-sm text-xs text-gray-500">
                  {feedback.length}/500 karakter
                </span>
                <span className="lg:text-sm text-xs text-gray-500">
                  Minimal 5 karakter
                </span>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-teal-600 hover:bg-teal-700 text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  <span>Mengirim...</span>
                </>
              ) : (
                <>
                  <Send className="lg:w-5 w-4 lg:h-5 h-4 " />
                  <span className="text-xs lg:text-sm">Kirim Feedback</span>
                </>
              )}
            </button>
            < div className="text-center mt-4">
            <a className='text-xs text-gray-500 ' href={clientUrl}>Pergi ke <span className='text-teal-700'>SIE 1 KETAQWAAN MAN 1 JEMBER</span></a>
            </div>
          </form>
        </div>

        {/* View Feedback Button */}
        <div className="text-center">
          <button
            onClick={() => setShowFeedbackList(!showFeedbackList)}
            className="bg-white hover:bg-gray-50 text-gray-700 font-semibold py-3 px-6 rounded-lg border border-gray-300 transition-all duration-200 flex items-center space-x-2 mx-auto"
          >
            <Eye className="lg:w-5 w-4 lg:h-5 h-4" />
            <span className="text-xs lg:text-sm">{showFeedbackList ? 'Sembunyikan' : 'Lihat Semua'} Feedback</span>
          </button>
        </div>

        {/* Feedback List */}
        {showFeedbackList && (
          <div className="mt-8">
            <FeedbackList />
          </div>
        )}
      </div>
    </div>
  );
};

export default FeedbackForm;